import React, { useEffect, useRef, useState, useMemo } from "react";
import { X, Search } from "lucide-react";
import { usePlaces } from "../context/PlacesContext";
import { PlaceCard } from "./Bits";

export default function SearchOverlay({ open, onClose }) {
  const { places } = usePlaces();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 60);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return places.filter((p) => {
      const hay = [
        p.name, p.city, p.region, p.country, p.category, p.cuisine, p.neighborhood,
        ...(p.tags || []),
      ].join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [q, places]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-paper flex flex-col rise-in">
      <div className="border-b border-ink/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center gap-4 h-[84px]">
          <Search className="w-6 h-6 text-claret shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by place, city, or tag…"
            className="flex-1 bg-transparent text-2xl md:text-3xl font-serif outline-none placeholder-ink-soft/40"
          />
          <button onClick={onClose} aria-label="Close search" className="text-ink-soft hover:text-ink">
            <X className="w-7 h-7" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          {!q.trim() ? (
            <p className="text-ink-soft text-lg">Start typing to search every reviewed place.</p>
          ) : results.length ? (
            <>
              <p className="text-sm text-ink-soft mb-8">
                {results.length} {results.length === 1 ? "result" : "results"} for "{q}"
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14" onClick={onClose}>
                {results.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
              </div>
            </>
          ) : (
            <div className="py-16">
              <p className="font-serif text-3xl">No places match "{q}".</p>
              <p className="text-ink-soft mt-2">Try a place name, a city, or a tag like "Coffee" or "Date Night".</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
